import React, { useEffect, useMemo, useState } from "react";
import LocationsEditor from "./LocationsEditor";
import { Button, Form, Modal, Spinner } from "react-bootstrap";
import { useDispatch } from "react-redux";
import {
  nginxAddSubdomain,
  nginxDnsContext,
  nginxDnsUpsertRecord,
  nginxListCerts,
  nginxProvisionSsl,
  nginxProvisionWildcard,
} from "../../redux/actionCreator";

/**
 * Two-step wizard:
 *   1. Validate inputs, write a server block, nginx -t, reload.
 *   2. (optional) Provision SSL via certbot DNS-01 (Cloudflare), then
 *      rewrite the vhost to HTTPS and reload again.
 *
 * Closes only on the user's action — we keep the modal open during the
 * SSL step so the user sees certbot output even after the vhost write
 * succeeds.
 */
// Suffixes that can never hold a public certificate. ".conf" is first
// because every vhost file here is named "<fqdn>.conf", so pasting a
// filename into this box is the easy mistake — and it only surfaced as a
// confusing Let's Encrypt error after a wasted DNS-01 attempt.
const NON_PUBLIC_SUFFIXES = new Set([
  "conf", "config", "cfg", "yml", "yaml", "json", "txt", "bak", "tmp",
  "disabled", "sample", "example", "template",
  "local", "localhost", "localdomain", "test", "invalid", "internal",
  "lan", "home", "intranet", "corp",
]);

function suffixProblem(fqdn) {
  const name = String(fqdn || "").trim().toLowerCase().replace(/\.$/, "");
  if (!name.includes(".")) return null;
  const tld = name.split(".").pop();
  if (tld === "conf") {
    return 'That looks like a vhost filename, not a domain — drop the ".conf".';
  }
  if (NON_PUBLIC_SUFFIXES.has(tld)) {
    return `".${tld}" is not a public domain suffix, so no certificate can be issued for it.`;
  }
  if (!/^[a-z]{2,24}$/.test(tld)) {
    return `".${tld}" is not a valid top-level domain.`;
  }
  return null;
}

export default function AddSubdomainModal({
  servers = [],
  show,
  onHide,
  project,
  server,
  onDone,
}) {
  const dispatch = useDispatch();
  const [fqdn, setFqdn] = useState("");
  const [upstream, setUpstream] = useState("http://127.0.0.1:3000");
  const [extra, setExtra] = useState("");
  const [locations, setLocations] = useState([]);
  // Gate the site behind a portal login: nginx only answers IPs that
  // have completed MFA. Off by default — a public site must not become
  // unreachable because someone ticked a box they didn't understand.
  const [requireLogin, setRequireLogin] = useState(false);
  // "existing" reuses a certbot lineage already on the box (normally a
  // wildcard) and needs no certbot run at all; "new" issues a per-subdomain
  // cert as before; "none" leaves the vhost on plain HTTP.
  // Cloudflare DNS. The server already holds an API token for certbot's
  // DNS-01 challenge; the portal drives the same API through it, so a new
  // subdomain no longer means a detour to the Cloudflare dashboard.
  const [dns, setDns] = useState(null);      // { configured, zones, public_ip }
  const [makeDns, setMakeDns] = useState(true);
  const [dnsTarget, setDnsTarget] = useState("");
  const [dnsProxied, setDnsProxied] = useState(false);

  const [sslMode, setSslMode] = useState("new");
  const [certs, setCerts] = useState([]);
  const [certDir, setCertDir] = useState("");

  useEffect(() => {
    if (!show) return;
    let alive = true;
    (async () => {
      const res = await dispatch(nginxListCerts(project._id, server._id));
      if (alive && res?.status) setCerts(res.data?.certs || []);
      const d = await dispatch(nginxDnsContext(project._id, server._id));
      if (!alive) return;
      if (d?.status) {
        setDns(d.data);
        if (d.data?.public_ip) setDnsTarget(d.data.public_ip);
        setMakeDns(!!d.data?.configured);
      } else {
        setDns({ configured: false, zones: [] });
        setMakeDns(false);
      }
    })();
    return () => { alive = false; };
  }, [show, project?._id, server?._id, dispatch]);

  // Longest matching zone, mirroring what the server does.
  const dnsZone = useMemo(() => {
    const name = fqdn.trim().toLowerCase();
    let best = null;
    for (const z of (dns?.zones || [])) {
      const zn = String(z.name).toLowerCase();
      if (name === zn || name.endsWith("." + zn)) {
        if (!best || zn.length > best.name.length) best = z;
      }
    }
    return best;
  }, [dns, fqdn]);

  // A wildcard matches exactly ONE label: *.example.com covers
  // api.example.com but not a.b.example.com, and not the apex unless the
  // apex was requested alongside it.
  const usableCerts = useMemo(() => {
    const name = fqdn.trim().toLowerCase();
    if (!name) return [];
    return certs.filter((c) =>
      (c.domains || []).some((d) => {
        const dd = String(d).toLowerCase();
        if (dd === name) return true;
        if (!dd.startsWith("*.")) return false;
        return name.endsWith(dd.slice(1)) && name.split(".").length === dd.split(".").length;
      }),
    );
  }, [certs, fqdn]);

  // The wildcard that WOULD cover this name. For api.example.com that is
  // *.example.com; for a.b.example.com it is *.b.example.com, because a
  // wildcard only ever matches one label. Offered when nothing on the box
  // covers the name yet, so the gap is closed where it shows up rather
  // than by sending the operator to another screen.
  const fqdnProblem = useMemo(() => suffixProblem(fqdn), [fqdn]);

  const parentWildcard = useMemo(() => {
    const name = fqdn.trim().toLowerCase();
    const parts = name.split(".").filter(Boolean);
    // Needs a label to strip AND a registrable parent left over, so
    // "example.com" yields nothing (its parent would be the TLD).
    if (parts.length < 3) return null;
    return parts.slice(1).join(".");
  }, [fqdn]);

  // Prefer a wildcard when one covers this name — that's the cheap path.
  useEffect(() => {
    if (!usableCerts.length) { if (sslMode === "existing") setSslMode("new"); return; }
    if (!certDir || !usableCerts.some((c) => c.dir === certDir)) {
      const wc = usableCerts.find((c) => c.wildcard) || usableCerts[0];
      setCertDir(wc.dir);
      setSslMode("existing");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [usableCerts]);
  const [busy, setBusy] = useState(false);
  const [step, setStep] = useState("form"); // form | vhost-ok | ssl-ok | error
  const [message, setMessage] = useState(null);
  const [details, setDetails] = useState(null);

  const reset = () => {
    setFqdn("");
    setUpstream("http://127.0.0.1:3000");
    setExtra("");
    setSslMode("new");
    setCertDir("");
    setLocations([]);
    setRequireLogin(false);
    setBusy(false);
    setStep("form");
    setMessage(null);
    setDetails(null);
  };

  const close = () => {
    if (busy) return;
    reset();
    onHide && onHide();
  };

  const submit = async () => {
    setBusy(true);
    setMessage(null);
    setDetails(null);

    // DNS first: the name has to resolve for the site to work, and doing
    // it before the slow certbot step means a bad zone fails in a second
    // rather than after a DNS-01 round trip.
    if (makeDns && dns?.configured) {
      setStep("vhost-ok");
      setMessage(`Creating DNS record for ${fqdn.trim()}…`);
      const rec = await dispatch(nginxDnsUpsertRecord(project._id, server._id, {
        fqdn: fqdn.trim(),
        type: "A",
        content: dnsTarget.trim() || undefined,
        proxied: dnsProxied,
      }));
      if (!rec?.status) {
        setStep("error");
        setMessage(rec?.message || "Could not create the DNS record");
        setBusy(false);
        return;
      }
    }

    // "wildcard" issues *.<parent> FIRST, because the vhost is written as
    // HTTPS and would fail nginx -t against a cert that isn't there yet.
    let useCertDir = sslMode === "existing" ? certDir : "";
    if (sslMode === "wildcard") {
      setStep("vhost-ok");
      setMessage(`Issuing wildcard for *.${parentWildcard} — DNS-01 can take a minute…`);
      const wc = await dispatch(
        nginxProvisionWildcard(project._id, server._id, {
          domain: parentWildcard,
          include_apex: true,
        }),
      );
      if (!wc?.status || !wc?.data?.cert_dir) {
        setStep("error");
        setMessage(wc?.message || "Wildcard issuance failed");
        setDetails(wc?.data?.output);
        setBusy(false);
        return;
      }
      useCertDir = wc.data.cert_dir;
    }

    const vhost = await dispatch(
      nginxAddSubdomain(project._id, server._id, {
        fqdn: fqdn.trim(),
        upstream: upstream.trim(),
        extra_locations: extra.trim() || undefined,
        locations,
        cert_dir: useCertDir || undefined,
        require_login: requireLogin,
      })
    );
    if (!vhost?.status) {
      setStep("error");
      setMessage(vhost?.message || "Failed to add subdomain");
      setDetails(vhost?.data?.test_output);
      setBusy(false);
      return;
    }

    if (useCertDir) {
      // The vhost was written as HTTPS against the lineage directly —
      // there is nothing left for certbot to do.
      setStep("ssl-ok");
      setMessage(
        sslMode === "wildcard"
          ? `Wildcard *.${parentWildcard} issued and ${fqdn.trim()} created on HTTPS. Future subdomains under it need no certbot run.`
          : `Subdomain ${fqdn.trim()} created on HTTPS using the existing certificate (${useCertDir}).`,
      );
      setBusy(false);
      onDone && onDone();
      return;
    }

    if (sslMode === "none") {
      setStep("vhost-ok");
      setMessage("Subdomain created. Skipping SSL.");
      setBusy(false);
      onDone && onDone();
      return;
    }

    setStep("vhost-ok");
    setMessage(`Subdomain ${fqdn.trim()} created. Provisioning SSL…`);
    const ssl = await dispatch(
      nginxProvisionSsl(project._id, server._id, {
        fqdn: fqdn.trim(),
        rewrite_vhost: true,
      })
    );
    setBusy(false);
    if (!ssl?.status) {
      setStep("error");
      setMessage(ssl?.message || "certbot failed");
      setDetails(ssl?.data?.output);
      return;
    }
    setStep("ssl-ok");
    setMessage(`HTTPS enabled for ${fqdn.trim()}.`);
    setDetails(ssl?.data?.output);
    onDone && onDone();
  };

  const valid =
    /^[A-Za-z0-9.-]{3,253}$/.test(fqdn.trim()) &&
    !fqdnProblem &&
    /^https?:\/\/.+/.test(upstream.trim());

  return (
    <Modal show={show} onHide={close} size="lg" centered backdrop="static">
      <Modal.Header closeButton={!busy}>
        <h4 className="m-0">Add Subdomain</h4>
      </Modal.Header>
      <Modal.Body>
        {step === "form" && (
          <>
            <Form.Group className="mb-3">
              <Form.Label>Fully-qualified domain *</Form.Label>
              <Form.Control
                value={fqdn}
                onChange={(e) => setFqdn(e.target.value)}
                placeholder="demo.crm.shtops.xyz"
                style={{ fontFamily: "ui-monospace, monospace" }}
              />
            {fqdnProblem && (
              <div className="small text-danger mt-1">{fqdnProblem}</div>
            )}
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Upstream URL *</Form.Label>
              <Form.Control
                value={upstream}
                onChange={(e) => setUpstream(e.target.value)}
                placeholder="http://127.0.0.1:3000"
                style={{ fontFamily: "ui-monospace, monospace" }}
              />
              <small className="text-muted">
                Where nginx should proxy traffic to. Usually a local port
                (your app or a docker container).
              </small>
            </Form.Group>
            <Form.Group className="mb-3">
              <LocationsEditor
                value={locations}
                onChange={setLocations}
                projectId={project?._id}
                serverId={server?._id}
                servers={servers}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>
                Extra location blocks{" "}
                <small className="text-muted">(optional, raw nginx — for anything the builder above can't express)</small>
              </Form.Label>
              <Form.Control
                as="textarea"
                rows={4}
                value={extra}
                onChange={(e) => setExtra(e.target.value)}
                placeholder={`location /api/ {\n  proxy_pass http://127.0.0.1:8080;\n}`}
                style={{ fontFamily: "ui-monospace, monospace", fontSize: 12 }}
              />
            </Form.Group>
            {/* DNS. Without this the operator creates the record by hand in
                Cloudflare before every subdomain — the server already has a
                token for certbot, so the portal can just do it. */}
            <Form.Label className="mb-1">DNS</Form.Label>
            {dns === null ? (
              <div className="small text-muted mb-3">Checking Cloudflare…</div>
            ) : !dns.configured ? (
              <div className="small text-muted mb-3">
                No Cloudflare credentials path is set on this server, so the DNS
                record has to be created by hand. Set one in the server's nginx
                settings to do it from here.
              </div>
            ) : (
              <div className="mb-3">
                <Form.Check
                  type="switch" id="dns-create" checked={makeDns}
                  onChange={(e) => setMakeDns(e.target.checked)}
                  label={
                    dnsZone
                      ? <>Create <code>A {fqdn.trim() || "<name>"}</code> in zone <code>{dnsZone.name}</code></>
                      : <span className="text-warning">No Cloudflare zone here covers that name</span>
                  }
                  disabled={!dnsZone}
                />
                {makeDns && dnsZone && (
                  <div className="d-flex gap-2 align-items-center mt-2 flex-wrap">
                    <Form.Control
                      size="sm" value={dnsTarget} onChange={(e) => setDnsTarget(e.target.value)}
                      placeholder="server public IP"
                      style={{ fontFamily: "ui-monospace, monospace", maxWidth: 180 }}
                    />
                    <span className="small text-muted">
                      {dns.public_ip
                        ? <>detected <code>{dns.public_ip}</code></>
                        : <span className="text-warning">couldn't detect the public IP — enter it</span>}
                    </span>
                    <Form.Check
                      type="switch" id="dns-proxied" className="ms-auto"
                      checked={dnsProxied} onChange={(e) => setDnsProxied(e.target.checked)}
                      label={<span className="small">Proxy through Cloudflare</span>}
                    />
                  </div>
                )}
                {makeDns && dnsZone && dnsProxied && (
                  <div className="small text-muted mt-1">
                    Proxied means Cloudflare terminates TLS and only HTTP(S)
                    reaches the origin. Leave it off for an API or anything
                    non-HTTP until you've confirmed it behaves.
                  </div>
                )}
                {!dnsZone && dns.zones?.length > 0 && (
                  <div className="small text-muted mt-1">
                    This token can see: {dns.zones.map((z) => <code key={z.id} className="me-1">{z.name}</code>)}
                  </div>
                )}
              </div>
            )}

            <Form.Check
              type="switch"
              id="require-login"
              className="mb-3"
              checked={requireLogin}
              onChange={(e) => setRequireLogin(e.target.checked)}
              label={<>Require a portal login to reach this site</>}
            />
            {requireLogin && (
              <div className="small text-muted mb-3">
                nginx will answer only IPs that have completed a portal login,
                and the list is cleared every night — so everyone has to log in
                again each day to regain access. Only works on the server the
                portal itself runs on, which is where the allowlist is written.
              </div>
            )}
            <Form.Label className="mb-1">Certificate</Form.Label>
            <Form.Check
              type="radio" name="sslmode" id="ssl-existing"
              checked={sslMode === "existing"}
              disabled={!usableCerts.length}
              onChange={() => setSslMode("existing")}
              label={
                usableCerts.length
                  ? <>Use a certificate already on this server <span className="text-success">(no certbot run)</span></>
                  : <span className="text-muted">No existing certificate covers this name</span>
              }
            />
            {sslMode === "existing" && usableCerts.length > 0 && (
              <Form.Select
                size="sm" className="mt-1 mb-2" value={certDir}
                onChange={(e) => setCertDir(e.target.value)}
                style={{ fontFamily: "ui-monospace, monospace" }}
              >
                {usableCerts.map((c) => (
                  <option key={c.dir} value={c.dir}>
                    {c.wildcard ? "★ " : ""}{c.domains.join(", ")} — expires {c.expiry || "?"}
                  </option>
                ))}
              </Form.Select>
            )}
            {parentWildcard && (
              <Form.Check
                type="radio" name="sslmode" id="ssl-wildcard"
                checked={sslMode === "wildcard"}
                onChange={() => setSslMode("wildcard")}
                label={<>Issue a wildcard <code>*.{parentWildcard}</code> — also covers every future sibling</>}
              />
            )}
            <Form.Check
              type="radio" name="sslmode" id="ssl-new"
              checked={sslMode === "new"}
              onChange={() => setSslMode("new")}
              label="Issue a new Let's Encrypt certificate for this subdomain only (Cloudflare DNS-01)"
            />
            <Form.Check
              type="radio" name="sslmode" id="ssl-none"
              checked={sslMode === "none"}
              onChange={() => setSslMode("none")}
              label="No SSL — plain HTTP"
            />
            <small className="text-muted d-block mt-1">
              A wildcard covers every subdomain at one level, so reusing it is
              instant and doesn't spend one of the 50 certificates Let's Encrypt
              allows per domain per week. Issuing a new one runs certbot with the
              credentials and email configured on this server.
            </small>
          </>
        )}

        {step !== "form" && (
          <>
            <div
              className={`alert ${
                step === "error"
                  ? "alert-danger"
                  : step === "ssl-ok"
                  ? "alert-success"
                  : "alert-info"
              }`}
            >
              {busy && (
                <Spinner size="sm" animation="border" className="me-2" />
              )}
              {message}
            </div>
            {details && (
              <pre
                style={{
                  background: "#0b1220",
                  color: "#e2e8f0",
                  padding: 14,
                  borderRadius: 8,
                  maxHeight: 320,
                  overflow: "auto",
                  fontSize: 12,
                }}
              >
                {details}
              </pre>
            )}
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="light" onClick={close} disabled={busy}>
          Close
        </Button>
        {step === "form" && (
          <Button onClick={submit} disabled={!valid || busy} variant="primary">
            {busy ? <Spinner size="sm" animation="border" /> : "Create"}
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
}
